import React from 'react';
import { i18next, utils, view } from '@yola/ws-sdk';
import { designSystem } from '@yola/ws-ui';
import optionUnits from '../constants/option-units';
import optionTypes from '../constants/option-types';
import previewGifs from '../constants/preview-gifs';

const { Paragraph, Icon } = designSystem;

const DEFAULT_PROPERTY = '--ws-media-container-border-radius';
const FALLBACK_VALUE = 0;

const SUPPORTED_UNITS = [optionUnits.PX, optionUnits.PERCENT];
const DEFAULT_UNIT = optionUnits.PX;

const PX_RANGE = {
  min: 0,
  max: 128,
  step: 1,
};
const PERCENT_RANGE = {
  min: 0,
  max: 100,
  step: 1,
};

const findUnit = (value) => SUPPORTED_UNITS.find((unit) => `${value}`.endsWith(unit)) || '';

const getRange = (value) => {
  const unit = findUnit(value);

  switch (unit) {
    case optionUnits.PX:
      return PX_RANGE;
    case optionUnits.PERCENT:
      return PERCENT_RANGE;
    default:
      return PX_RANGE;
  }
};
const getMin = (value) => getRange(value).min;
const getMax = (value) => getRange(value).max;
const getStep = (value) => getRange(value).step;

const getUnit = (value) => {
  const unit = findUnit(value);

  return unit === DEFAULT_UNIT ? '' : unit || '';
};

const transformValue = (value) => {
  const unit = findUnit(value);

  return unit ? value : `${value}${DEFAULT_UNIT}`;
};

const computeFallbackValue = () => {
  const liveDom = view.accessors.getLiveDocument();

  const defaultValue = getComputedStyle(liveDom.documentElement).getPropertyValue(DEFAULT_PROPERTY);

  if (!defaultValue) return FALLBACK_VALUE;

  if (defaultValue.endsWith(optionUnits.PERCENT)) {
    return defaultValue;
  }

  const normalizedValue = parseFloat(defaultValue);

  return utils.isNumeric(normalizedValue) ? normalizedValue : FALLBACK_VALUE;
};

const CORNERS = [
  {
    id: 'top-left-corner',
    iconGlyph: 'corner-top-left-radius',
    property: '--ws-media-container-custom-border-top-left-radius',
  },
  {
    id: 'top-right-corner',
    iconGlyph: 'corner-top-right-radius',
    property: '--ws-media-container-custom-border-top-right-radius',
  },
  {
    id: 'bottom-left-corner',
    iconGlyph: 'corner-bottom-left-radius',
    property: '--ws-media-container-custom-border-bottom-left-radius',
  },
  {
    id: 'bottom-right-corner',
    iconGlyph: 'corner-bottom-right-radius',
    property: '--ws-media-container-custom-border-bottom-right-radius',
  },
];

const getPopoverProps = ({ imgSrc, iconGlyph }) => ({
  imgSrc,
  renderContent: () => (
    <Paragraph appearance="white" size="small">
      {i18next.t('Adjust corners for media')}. {i18next.t('Click the {icon}', { icon: '' })}{' '}
      <Icon type="white" size={16} strokeWidth={2} glyph={iconGlyph} insideText />
      {i18next.t('{click_the_icon} to adjust each one separately', { click_the_icon: '' })}
    </Paragraph>
  ),
});

const getMediaCornersSettings = () => ({
  id: 'media-corners',
  type: optionTypes.SLIDER,
  title: i18next.t('Independent corners'),
  mixedLabel: i18next.t('Mixed'),
  iconGlyph: 'corner-radius',
  step: getStep,
  min: getMin,
  max: getMax,
  supportedUnits: SUPPORTED_UNITS,
  popover: getPopoverProps({
    imgSrc: previewGifs.MEDIA_CORNERS_GIF,
    iconGlyph: 'corner-radius',
  }),
  getUnit,
  transformValue,
  computeFallbackValue,
  options: CORNERS,
});

export default getMediaCornersSettings;
